import { UserService } from '../../../service/user.service';
import { Component, computed, inject, input, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, Renderer2, Inject, HostBinding } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouterLink, ActivatedRoute, NavigationEnd } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from '../../../service/auth.service';
import { ApplicationSessionStateService } from '../../../service/application-session-state.service';
import { clearLocalStorageSession } from '../../../service/local-storage-session.util';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment'
import { MailboxService } from '../../../service/mailbox.service';
import { GroupService } from '../../../service/group.service';
import { TeamService } from '../../../service/team.service';
import { TeamImaginusNavService, TeamHeaderAction } from '../../../service/team-imaginus-nav.service';
import { team, enterprise } from '../../../shared/shared.module'
import { Subscription, forkJoin } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  ThemeDirective, TableModule,
  GridModule, NavModule,
  // 補上 Modal 及 Button 等必要元件
  ModalComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
  ModalBodyComponent,
  ModalFooterComponent,
  ButtonCloseDirective,
  ButtonDirective,
  FormControlDirective,
  FormLabelDirective,
  SpinnerComponent
} from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { AppNavBackComponent } from '../../../shared/app-nav-back/app-nav-back.component';
import { HelpArticleLinkComponent } from '../../../shared/help-article-link.component';
import { HELP_ARTICLES } from '../../../shared/help-article-ids';
import { SidebarStateService } from '../../../service/sidebar-state.service';
import { TranslateModule } from '@ngx-translate/core';
import { LocaleService } from '../../../service/locale.service';
import { TokenStorageService } from '@services/token-storage.service';
import {
  cilEnvelopeClosed, cilCalendarCheck, cilMoney, cilUser, cilGroup, cilPlus,
  cilAccountLogout, cilBuilding, cilMenu
} from '@coreui/icons';

interface User {
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  profile_picture?: string;
  notification_method: string[];
  role: string[];
}

@Component({
  selector: 'app-team-header',
  templateUrl: './team-header.component.html',
  styleUrls: ['./team-header.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    GridModule,
    NavModule,
    ContainerComponent,
    HeaderNavComponent,
    HeaderTogglerDirective,
    IconDirective,
    RouterLink,
    ThemeDirective,
    DropdownComponent,
    DropdownToggleDirective,
    DropdownMenuDirective,
    DropdownItemDirective,
    // 註冊 Modal 與表單元件
    ModalComponent, ModalHeaderComponent, ModalTitleDirective, ModalBodyComponent, ModalFooterComponent,
    ButtonCloseDirective, ButtonDirective, FormControlDirective, FormLabelDirective, SpinnerComponent,
    AppNavBackComponent,
    HelpArticleLinkComponent,
    TranslateModule,
  ]
})
export class TeamHeaderComponent extends HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly help = HELP_ARTICLES;
  public profile_picture: string = '';
  public userRoles: string[] = [];
  isMailboxOpen = false;
  hasUnreadMail: boolean = false;

  isSettingOpen = false;
  isSettingModalVisible: boolean = false;
  previewImage: string | null = null;
  showConfirmDialog = false;
  originalFormValue: any;
  selectedFile: File | null = null;
  formData!: FormGroup;
  production = environment.production;
  apiBaseUrl = environment.apiBaseUrl;
  filetooLarge = false;
  filenotJPG = false;
  visible = false;

  private mailSubscription!: Subscription;
  private imaginusTitleSub?: Subscription;
  private imaginusSubtitleSub?: Subscription;
  private imaginusActionsSub?: Subscription;
  private routerEventsSub?: Subscription;
  private lastAvatarTeamCode = '';
  private personalAvatarSub?: Subscription;
  private teamAvatarSub?: Subscription;

  /** Team Imaginus（/team/:code/home）頂欄：對話標題與操作 */
  showImaginusToolbar = false;

  @HostBinding('class.app-header--floating')
  get floatingHeader(): boolean {
    return this.showImaginusToolbar;
  }
  activeSessionTitle: string | null = null;
  activeSessionSubtitle: string | null = null;
  activeSessionActions: TeamHeaderAction[] = [];
  userMenuOpen = false;
  readonly userMenuPopperOptions = {
    placement: 'bottom-end' as const,
    strategy: 'fixed' as const,
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' as const } },
    ],
  };

  // --- 創建團隊 Modal 狀態 ---
  isCreateTeamModalVisible: boolean = false;
  newTeamName: string = '';
  isCreatingTeam: boolean = false;

  // 用來抓取需要 Teleport 的包裹元素
  @ViewChild('modalTeleportTarget', { static: false }) modalTeleportTarget!: ElementRef;

  user: User = {
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    profile_picture: undefined,
    notification_method: [],
    role: []
  };
  team: team = {
    team_code: '',
    team_name: '',
    accounts: []
  };
  enterprise_list: enterprise[] = [];
  team_list: team[] = [];
  team_code: string = '';

  readonly #colorModeService = inject(ColorModeService);
  private readonly locale = inject(LocaleService);
  readonly colorMode = this.#colorModeService.colorMode;

  readonly colorModes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' },
    { name: 'auto', text: 'Auto', icon: 'cilContrast' }
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
  });

  constructor(
    private authService: AuthService,
    private applicationSessionState: ApplicationSessionStateService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private fb: FormBuilder,
    private MailboxService: MailboxService,
    private groupService: GroupService,
    private teamService: TeamService,
    private teamImaginusNav: TeamImaginusNavService,
    private sidebarState: SidebarStateService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
    this.get_self_info();
    this.getUserRoles();
    this.fetchUserContext();
  }

  // 把 Modal 的容器傳送到 body 最外層，避免被 Header 的 z-index 擋住
  ngAfterViewInit(): void {
    if (this.modalTeleportTarget) {
      this.renderer.appendChild(this.document.body, this.modalTeleportTarget.nativeElement);
    }
  }

  ngOnInit(): void {
    this.profile_picture = this.defaultAvatarUrl;
    this.refreshPersonalAvatar();
    this.mailSubscription = this.MailboxService.unreadMail$.subscribe((status) => {
      this.hasUnreadMail = status;
    });

    this.route.paramMap.subscribe(params => {
      const newTeamCode = params.get('team_code');
      if (newTeamCode) {
        this.team_code = newTeamCode;
        this.loadHeaderTeamData(newTeamCode);
        this.getTeamAvatar(newTeamCode);
      }
      this.refreshImaginusToolbarVisibility();
    });

    this.imaginusTitleSub = this.teamImaginusNav.activeSessionTitle$.subscribe((t) => {
      this.activeSessionTitle = t;
      this.refreshImaginusToolbarVisibility();
    });
    this.imaginusSubtitleSub = this.teamImaginusNav.activeSessionSubtitle$.subscribe((s) => {
      this.activeSessionSubtitle = s;
    });
    this.imaginusActionsSub = this.teamImaginusNav.activeSessionActions$.subscribe((a) => {
      this.activeSessionActions = a || [];
    });
    this.routerEventsSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.refreshImaginusToolbarVisibility());
    this.refreshImaginusToolbarVisibility();
  }

  ngOnDestroy(): void {
    this.personalAvatarSub?.unsubscribe();
    this.teamAvatarSub?.unsubscribe();
    if (this.mailSubscription) {
      this.mailSubscription.unsubscribe();
    }
    this.imaginusTitleSub?.unsubscribe();
    this.imaginusSubtitleSub?.unsubscribe();
    this.imaginusActionsSub?.unsubscribe();
    this.routerEventsSub?.unsubscribe();
    // 元件銷毀時清掉移轉出去的 Modal DOM
    if (this.modalTeleportTarget && this.modalTeleportTarget.nativeElement) {
      this.renderer.removeChild(this.document.body, this.modalTeleportTarget.nativeElement);
    }
  }

  fetchUserContext() {
     this.groupService.get_user_enterprise().subscribe(
      (data) => {
        TokenStorageService.refreshToken(data.token);
        this.enterprise_list = [];
        for (let enterprise of data.enterprises) {
          this.enterprise_list.push({
            taxId: enterprise.taxId,
            name: enterprise.name,
            address: enterprise.address,
            telephone: enterprise.telephone
          });
        }
      },
      (error) => { console.error('Error fetching user enterprises:', error); }
    );

    this.loadTeamList();
  }

  // 獨立抽出的載入團隊列表方法
  loadTeamList() {
    this.groupService.get_user_team().subscribe(
      (data) => {
        TokenStorageService.refreshToken(data.token);
        this.team_list = [];
        for (let team of data.teams) {
          this.team_list.push({
            team_name: team.team_name,
            team_code: team.team_code,
            disabled: !!team.disabled,
            accounts: []
          });
        }
        this.syncUnifiedUnreadMail();
      },
      (error) => { console.error('Error fetching user teams:', error); }
    );
  }

  // --- 創建團隊相關邏輯 ---
  openCreateTeamModal() {
    this.newTeamName = '';
    this.userMenuOpen = false;
    this.isCreateTeamModalVisible = true;
  }

  closeCreateTeamModal() {
    this.isCreateTeamModalVisible = false;
    this.newTeamName = '';
  }

  submitCreateTeam() {
    if (!this.newTeamName.trim()) return;
    this.isCreatingTeam = true;

    this.groupService.create_team(this.newTeamName).subscribe({
      next: (response) => {
        if (response && response.token) {
          TokenStorageService.refreshToken(response.token);
        }
        this.loadTeamList();
        this.isCreatingTeam = false;
        this.closeCreateTeamModal();
      },
      error: (error) => {
        console.error('創建團隊失敗:', error);
        this.isCreatingTeam = false;
      }
    });
  }

  loadHeaderTeamData(code: string) {
    this.teamService.get_team_info(code).subscribe(
      (data) => {
        TokenStorageService.refreshToken(data.token);
        this.team = data.data;
        this.syncUnifiedUnreadMail();
      },
      (error) => {
        console.error('Header load failed', error);
      }
    );
  }

  get_self_info() {
    this.userService.get_self_info().subscribe(
      (data) => {
        if (!data?.user) {
          return;
        }
        this.user = data.user;
        TokenStorageService.refreshToken(data.token);
        this.refreshPersonalAvatar(true);
      },
      (error) => {}
    );
  }

  getUserRoles() {
    this.userService.getUserRoles().subscribe(
      (data) => {
        this.userRoles = data.roles;
        TokenStorageService.refreshToken(data.token);
      },
      (error) => {
        this.userRoles = [];
      }
    );
  }

  onlogout() {
    this.userMenuOpen = false;
    this.applicationSessionState.resetForLogout();
    clearLocalStorageSession();
    this.authService.logout();
    this.router.navigate(['/sign-in']);
  }

  sidebarId = input('sidebar1');

  get defaultAvatarUrl(): string {
    return this.userService.defaultAvatarUrl;
  }

  get headerAvatarUrl(): string {
    return this.previewImage || this.profile_picture || this.defaultAvatarUrl;
  }

  OpenMailbox(): void {
    this.userMenuOpen = false;
    const queryParams: Record<string, string> = {};
    const code = this.team?.team_code || this.team_code;
    if (code) {
      queryParams['team'] = code;
    }
    this.router.navigate(['/mailbox'], { queryParams });
  }

  private syncUnifiedUnreadMail(): void {
    const teamCodes = this.team_list.filter((t) => !t.disabled).map((t) => t.team_code);
    const requests = [
      this.MailboxService.loadMails(),
      ...teamCodes.map((code) => this.MailboxService.loadTeamMails(code)),
    ];
    forkJoin(requests).subscribe({
      next: (results) => {
        results.forEach((data: { token?: string }) => {
          if (data?.token) {
            TokenStorageService.refreshToken(data.token);
          }
        });
        const hasUnread = results.some((data) => this.MailboxService.hasUnreadInMailList(data));
        this.hasUnreadMail = hasUnread;
        this.MailboxService.updateUnreadMailStatus(hasUnread);
      },
    });
  }

  refreshPersonalAvatar(force = false): void {
    this.personalAvatarSub?.unsubscribe();
    this.personalAvatarSub = this.userService.loadPersonalAvatarUrl(force).subscribe({
      next: (url) => {
        this.profile_picture = url || this.defaultAvatarUrl;
      },
      error: () => {
        this.profile_picture = this.defaultAvatarUrl;
      },
    });
  }

  getUserAvatar() {
    this.refreshPersonalAvatar(true);
  }

  getTeamAvatar(team_code: string) {
    if (!team_code) {
      this.previewImage = null;
      return;
    }
    this.lastAvatarTeamCode = team_code;
    this.teamAvatarSub?.unsubscribe();
    this.teamAvatarSub = this.teamService.loadTeamAvatarUrl(team_code).subscribe({
      next: (url) => {
        this.previewImage = url || this.defaultAvatarUrl;
      },
      error: () => {
        this.previewImage = this.defaultAvatarUrl;
      },
    });
  }

  onAvatarImageError(): void {
    if (this.previewImage && this.previewImage.startsWith('blob:')) {
      this.previewImage = this.defaultAvatarUrl;
      return;
    }
    this.profile_picture = this.defaultAvatarUrl;
    this.previewImage = null;
  }

  private refreshImaginusToolbarVisibility(): void {
    const path = this.router.url.split('?')[0];
    const onTeamHome = /^\/team\/[^/]+\/home$/i.test(path);
    // 聊天室（App1 / App2）也把場域名稱顯示在中央 header 標題列
    const onChatroom = /^\/team\/[^/]+\/application\/[12]\/places\/[^/]+\/chatroom$/i.test(path);
    // 子應用（場域等）不佔中央標題列，避免長標題 + w-100 撐壞 flex、右側頭像被擠出窄螢幕
    this.showImaginusToolbar = onTeamHome || onChatroom;
  }

  /** 新對話／預設名稱頂欄留空，不顯示「新對話」佔位字 */
  get imaginusHeaderTitle(): string {
    const t = (this.activeSessionTitle || '').trim();
    if (!t) {
      return '';
    }
    if (t === '新對話' || t === '新交談') {
      return '';
    }
    const newDefault = this.locale.instant('layout.chat.newChatDefault');
    const newAlt = this.locale.instant('layout.chat.newChatAlt');
    if (t === newDefault || t === newAlt) {
      return '';
    }
    return t;
  }

  /** 觸發 header 上自訂動作鈕 */
  onHeaderActionClick(action: TeamHeaderAction, event: Event): void {
    event.stopPropagation();
    try {
      action?.onClick?.();
    } catch {
      /* swallow */
    }
  }

  trackByHeaderActionId(_: number, action: TeamHeaderAction): string {
    return action?.id ?? String(_);
  }

  /** 中央 header 主要動作 label：用於可點擊的 titlebox aria-label */
  get primaryHeaderActionLabel(): string {
    const first = this.activeSessionActions?.[0];
    return first?.ariaLabel || first?.title || first?.label || this.locale.instant('layout.header.viewDetails');
  }

  /** 點擊 header 標題區（Line 風格）：觸發第一個動作 */
  onPrimaryHeaderClick(event: Event): void {
    const first = this.activeSessionActions?.[0];
    if (!first) return;
    event.stopPropagation();
    try {
      first.onClick?.();
    } catch {
      /* swallow */
    }
  }

  /** 在 Imaginus 分頁點品牌列時導回「新對話」起點 */
  get imaHomeQueryParams(): { new?: string } {
    const path = this.router.url.split('?')[0];
    return /^\/team\/[^/]+\/home$/i.test(path) ? { new: '1' } : {};
  }

  get headerSidebarToggleLabel(): string {
    return this.locale.instant('layout.header.openMenu');
  }

  onHeaderSidebarToggle(): void {
    this.sidebarState.requestSidebarToggle('team');
  }

  navigateToIdentity(type: 'user' | 'enterprise' | 'team', id: string) {
    this.userMenuOpen = false;
    if (type === 'user') {
      this.router.navigate(['/home']);
    } else if (type === 'enterprise') {
      this.router.navigate(['/enterprise', id, 'home']);
    } else if (type === 'team') {
      const row = this.team_list.find((x) => x.team_code === id);
      if (row?.disabled) {
        alert(this.locale.instant('layout.header.teamDisabled'));
        return;
      }
      this.router.navigate(['/team', id, 'home']);
    }
  }
}
